import { useState } from "react";
import { motion } from "framer-motion";
import { Search, Loader2, PackageCheck, Hammer, Truck, CircleCheck } from "lucide-react";
import { toast } from "sonner";
import api, { money } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const STAGES = ["paid", "in_production", "shipped", "completed"];
const STAGE_META = {
  paid: { label: "Order received", icon: PackageCheck },
  in_production: { label: "At the workbench", icon: Hammer },
  shipped: { label: "Shipped", icon: Truck },
  completed: { label: "Delivered", icon: CircleCheck },
};

const Timeline = ({ status }) => {
  const idx = STAGES.indexOf(status);
  return (
    <div className="flex items-center gap-0 mt-6">
      {STAGES.map((s, i) => {
        const Icon = STAGE_META[s].icon;
        const active = i <= idx;
        return (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-2">
              <div className={`h-9 w-9 flex items-center justify-center border ${active ? "border-[#C87455] bg-[#C87455]/10 text-[#C87455]" : "border-zinc-800 text-zinc-700"}`}>
                <Icon size={15} strokeWidth={1.5} />
              </div>
              <span className={`font-mono-tech text-[9px] uppercase tracking-wider whitespace-nowrap ${active ? "text-zinc-300" : "text-zinc-700"}`}>
                {STAGE_META[s].label}
              </span>
            </div>
            {i < STAGES.length - 1 && <div className={`h-px flex-1 mx-2 mb-5 ${i < idx ? "bg-[#C87455]/60" : "bg-zinc-800"}`} />}
          </div>
        );
      })}
    </div>
  );
};

export default function Track() {
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/orders/track", { email: email.trim() });
      setOrders(data);
    } catch {
      toast.error("Could not look up orders — please try again.");
    }
    setLoading(false);
  };

  return (
    <main data-testid="track-page" className="pt-40 pb-32 min-h-screen">
      <Seo title="Track Your Order | Ember & Steel Studio" description="Check the status of your Ember & Steel Studio engraving order — received, engraving, shipped." path="/track" />
      <div className="max-w-3xl mx-auto px-6">
        <Reveal>
          <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">Order Tracking</span>
          <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight">
            Where's my piece?
          </h1>
          <p className="text-zinc-400 mt-6 max-w-xl">
            Enter the email you used at checkout and we'll show you exactly where your order
            is — from workbench to doorstep.
          </p>
        </Reveal>

        <form onSubmit={search} className="flex gap-3 mt-12" data-testid="track-form">
          <input
            data-testid="track-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="flex-1 bg-transparent border-b border-zinc-800 focus:border-[#C87455] transition-colors rounded-none px-0 py-3 text-white placeholder-zinc-600"
          />
          <button
            data-testid="track-submit-btn"
            type="submit"
            disabled={loading}
            className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-3 font-mono-tech uppercase tracking-[0.2em] text-sm flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Find
          </button>
        </form>

        {orders !== null && (
          <div className="mt-14 space-y-6">
            {orders.length === 0 ? (
              <p data-testid="track-no-orders" className="font-mono-tech text-sm text-zinc-500 uppercase tracking-widest">
                No orders found for that email
              </p>
            ) : (
              orders.map((o) => (
                <motion.div
                  key={o.ref}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0C0C0C] border border-zinc-900 p-6 md:p-8"
                  data-testid={`track-order-${o.ref}`}
                >
                  <div className="flex flex-wrap justify-between items-baseline gap-3">
                    <p className="font-mono-tech text-xs text-zinc-500 uppercase tracking-wider">
                      Order #{o.ref} · {new Date(o.created_at).toLocaleDateString()}
                    </p>
                    <span className="font-serif-display text-xl text-white">{money(o.amount)}</span>
                  </div>
                  <div className="mt-3 space-y-1">
                    {o.items.map((it, i) => (
                      <p key={i} className="text-sm text-zinc-300">
                        {it.quantity}× {it.name}
                        {it.engraving && <span className="text-[#C87455] font-mono-tech text-xs ml-2">"{it.engraving}"</span>}
                      </p>
                    ))}
                  </div>
                  {o.proof_status && o.proof_status !== "none" && (
                    <p className="font-mono-tech text-[10px] uppercase tracking-wider mt-3 text-zinc-500">
                      Proof:{" "}
                      <span className={o.proof_status === "approved" ? "text-emerald-400/80" : o.proof_status === "changes_requested" ? "text-amber-400/80" : "text-[#C87455]"}>
                        {o.proof_status.replace("_", " ")}
                      </span>
                    </p>
                  )}
                  <Timeline status={o.status} />
                </motion.div>
              ))
            )}
          </div>
        )}
      </div>
    </main>
  );
}
